import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dashboard,
  UpdateWidgets,
  Widget,
  useUpdateDashboardMutation,
  useUpdateWidgetMutation,
} from "shared/api";
import { Colors } from "shared/themes";
import { PollingData } from "shared/hooks/usePollingSeconds";
import { WidgetLayoutItem } from "./WidgetLayoutItem";

interface FreeWidgetPosition {
  uuid: string;
  x: number;
  y: number;
}

interface FreeLayoutProps {
  dashboardsData?: Dashboard;
  widgets: Widget[];
  themeColor: string;
  pollingData: PollingData;
}

const positionsCache = new Map<number, FreeWidgetPosition[]>();

const FreeLayout: FC<FreeLayoutProps> = ({
  dashboardsData,
  widgets,
  themeColor,
  pollingData,
}) => {
  const [updateDashboard] = useUpdateDashboardMutation();
  const [updateWidget] = useUpdateWidgetMutation();
  const dashboardId = dashboardsData?.id;
  const [positions, setPositions] = useState<FreeWidgetPosition[]>(
    () => (dashboardId ? positionsCache.get(dashboardId) : undefined) ?? []
  );
  const draggingRef = useRef<{
    uuid: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const positionsRef = useRef<FreeWidgetPosition[]>(
    (dashboardId ? positionsCache.get(dashboardId) : undefined) ?? []
  );
  const dashboardIdRef = useRef(dashboardId);
  dashboardIdRef.current = dashboardId;
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;
  const updateWidgetRef = useRef(updateWidget);
  updateWidgetRef.current = updateWidget;

  const validWidgets = useMemo(
    () =>
      (widgets ?? []).filter((w): w is Widget & { uuid: string } => !!w?.uuid),
    [widgets]
  );

  const widgetUuids = useMemo(
    () => validWidgets.map((w) => w.uuid).join(","),
    [validWidgets]
  );

  useEffect(() => {
    if (validWidgets.length > 0) {
      setPositions((prev) => {
        const cached = dashboardId
          ? positionsCache.get(dashboardId)
          : undefined;
        const base = prev.length > 0 ? prev : cached ?? [];
        const newPositions: FreeWidgetPosition[] = validWidgets.map(
          (widget, index) => {
            const existing = base.find((p) => p.uuid === widget.uuid);
            if (existing) return existing;

            if (widget.position_x != null && widget.position_y != null) {
              return {
                uuid: widget.uuid,
                x: widget.position_x,
                y: widget.position_y,
              };
            }

            const col = index % 2;
            const row = Math.floor(index / 2);
            return {
              uuid: widget.uuid,
              x: col * 520 + 10,
              y: row * 350 + 10,
            };
          }
        );
        positionsRef.current = newPositions;
        if (dashboardId) positionsCache.set(dashboardId, newPositions);
        return newPositions;
      });
    }
  }, [widgetUuids, dashboardId]);

  const removeWidget = useCallback(
    async (widgetId: string) => {
      const filteredWidgets = dashboardsData?.widgets?.filter(
        (widget) => widget.uuid !== widgetId
      );

      await updateDashboard({
        id: dashboardsData?.id!,
        name: dashboardsData?.name!,
        widgets: filteredWidgets as UpdateWidgets[],
        configuration: dashboardsData?.configuration ?? [],
      });
    },
    [dashboardsData, updateDashboard]
  );

  const DRAG_THRESHOLD = 5;

  const handleMouseDown = useCallback((e: React.MouseEvent, uuid: string) => {
    const pos = positionsRef.current.find((p) => p.uuid === uuid);
    if (!pos) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const origX = pos.x;
    const origY = pos.y;
    let isDragging = false;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
          return;
        }
        isDragging = true;
        draggingRef.current = {
          uuid,
          startX,
          startY,
          origX,
          origY,
        };
      }

      moveEvent.preventDefault();

      const newPositions = positionsRef.current.map((p) =>
        p.uuid === uuid
          ? {
              ...p,
              x: origX + dx,
              y: Math.max(0, origY + dy),
            }
          : p
      );
      positionsRef.current = newPositions;
      if (dashboardIdRef.current)
        positionsCache.set(dashboardIdRef.current, newPositions);
      setPositions(newPositions);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);

      if (isDragging) {
        const finalPos = positionsRef.current.find((p) => p.uuid === uuid);
        const widget = (widgetsRef.current ?? []).find((w) => w.uuid === uuid);
        if (finalPos && widget?.id) {
          updateWidgetRef.current({
            ...widget,
            position_x: Math.round(finalPos.x),
            position_y: Math.round(finalPos.y),
          });
        }
      }

      draggingRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const getPosition = (uuid: string) => {
    return positions.find((p) => p.uuid === uuid) ?? { x: 0, y: 0 };
  };

  const [collapsedWidgets, setCollapsedWidgets] = useState<Set<string>>(
    new Set()
  );

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`collapsed-widgets-${dashboardId}`);
      if (stored) {
        setCollapsedWidgets(new Set(JSON.parse(stored)));
      }
    } catch {
      // Ignore errors
    }
  }, [dashboardId]);

  // Save collapsed state to localStorage when it changes
  useEffect(() => {
    try {
      if (dashboardId) {
        localStorage.setItem(
          `collapsed-widgets-${dashboardId}`,
          JSON.stringify(Array.from(collapsedWidgets))
        );
      }
    } catch {
      // Ignore errors
    }
  }, [collapsedWidgets, dashboardId]);

  const handleCollapsedChange = useCallback(
    (uuid: string, collapsed: boolean) => {
      setCollapsedWidgets((prev) => {
        const next = new Set(prev);
        if (collapsed) {
          next.add(uuid);
        } else {
          next.delete(uuid);
        }
        return next;
      });
    },
    []
  );

  const maxY = positions.reduce((max, p) => Math.max(max, p.y), 0);
  const containerHeight = Math.max(600, maxY + 400);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        minHeight: containerHeight,
        background: Colors.gray[100],
        padding: 8,
      }}
    >
      {validWidgets.map((widget) => {
        const pos = getPosition(widget.uuid);
        return (
          <div
            key={widget.uuid}
            role="button"
            tabIndex={0}
            onKeyDown={() => {}}
            onMouseDown={(e) => handleMouseDown(e, widget.uuid)}
            style={{
              position: "absolute",
              left: pos.x,
              top: pos.y,
              width: "fit-content",
              minWidth: "480px",
              minHeight: "200px",
              userSelect: "none",
              padding: collapsedWidgets.has(widget.uuid) ? 0 : 16,
              display: "flex",
              flexDirection: "column",
              background: collapsedWidgets.has(widget.uuid)
                ? "transparent"
                : themeColor,
              cursor: "grab",
              zIndex: draggingRef.current?.uuid === widget.uuid ? 10 : 1,
              boxShadow: collapsedWidgets.has(widget.uuid)
                ? "none"
                : "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <WidgetLayoutItem
              widget={widget}
              onClick={() => removeWidget(widget.uuid)}
              pollingData={pollingData}
              onCollapsedChange={(c) => handleCollapsedChange(widget.uuid, c)}
              collapsed={collapsedWidgets.has(widget.uuid)}
            />
          </div>
        );
      })}
    </div>
  );
};

const FreeLayoutMemo = React.memo(FreeLayout);

export { FreeLayoutMemo as FreeLayout };
